/**
 * Universal Design System for Kingdom Bible Quiz
 * Maintains premium glassmorphism design across all platforms
 *
 * Usage:
 * - WhatsApp shareable quizzes
 * - Progressive Web App
 * - Future platform integrations (Telegram, Facebook, Instagram)
 */

export interface PlatformCapabilities {
  supportsGradients: boolean;
  supportsBlur: boolean;
  supportsTransparency: boolean;
  supportsShadows: boolean;
  supportsCustomFonts: boolean;
  maxImageSize: number;
  preferredAspectRatio: string;
}

export const PlatformProfiles: Record<string, PlatformCapabilities> = {
  whatsapp: {
    supportsGradients: true,
    supportsBlur: true,
    supportsTransparency: true,
    supportsShadows: true,
    supportsCustomFonts: true,
    maxImageSize: 1200,
    preferredAspectRatio: '1.91:1', // Link preview
  },
  telegram: {
    supportsGradients: true,
    supportsBlur: true,
    supportsTransparency: true,
    supportsShadows: true,
    supportsCustomFonts: true,
    maxImageSize: 1280,
    preferredAspectRatio: '16:9',
  },
  facebook: {
    supportsGradients: true,
    supportsBlur: true,
    supportsTransparency: true,
    supportsShadows: true,
    supportsCustomFonts: true,
    maxImageSize: 1200,
    preferredAspectRatio: '1.91:1',
  },
  instagram: {
    supportsGradients: true,
    supportsBlur: false, // Limited in stories
    supportsTransparency: true,
    supportsShadows: false, // Stories limitation
    supportsCustomFonts: false, // Native fonts only
    maxImageSize: 1080,
    preferredAspectRatio: '9:16', // Stories
  },
  tiktok: {
    supportsGradients: true,
    supportsBlur: true,
    supportsTransparency: true,
    supportsShadows: true,
    supportsCustomFonts: true,
    maxImageSize: 1080,
    preferredAspectRatio: '9:16', // Vertical video format
  },
};

/**
 * Core Design Tokens - Premium Glassmorphism Theme
 */
export const KingdomBibleQuizDesign = {
  // Brand Colors
  colors: {
    primary: '#6366F1',
    secondary: '#8B5CF6',
    tertiary: '#0EA5E9',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EC4899',

    // Glassmorphism Effects
    glass: {
      primary: 'rgba(255, 255, 255, 0.15)',
      secondary: 'rgba(255, 255, 255, 0.1)',
      border: 'rgba(255, 255, 255, 0.2)',
      backdrop: 'rgba(255, 255, 255, 0.05)',
    },

    // Text Colors
    text: {
      primary: '#FFFFFF',
      secondary: 'rgba(255, 255, 255, 0.9)',
      tertiary: 'rgba(255, 255, 255, 0.8)',
      caption: 'rgba(255, 255, 255, 0.7)',
    },

    // Background Gradients
    gradients: {
      main: ['#667eea', '#764ba2'],
      premium: ['#667eea', '#764ba2', '#8B5CF6'],
      success: ['#10B981', '#059669'],
      warning: ['#F59E0B', '#D97706'],
      danger: ['#EC4899', '#BE185D'],
    },
  },

  // Typography Scale
  typography: {
    // Headings
    heading: {
      fontSize: 24,
      fontWeight: '800',
      color: '#FFFFFF',
      lineHeight: 1.2,
      letterSpacing: -0.5,
    },
    subheading: {
      fontSize: 20,
      fontWeight: '700',
      color: '#FFFFFF',
      lineHeight: 1.3,
      letterSpacing: -0.25,
    },

    // Body Text
    body: {
      fontSize: 16,
      fontWeight: '600',
      color: '#FFFFFF',
      lineHeight: 1.4,
    },
    bodySecondary: {
      fontSize: 14,
      fontWeight: '500',
      color: 'rgba(255, 255, 255, 0.9)',
      lineHeight: 1.4,
    },

    // UI Elements
    button: {
      fontSize: 16,
      fontWeight: '700',
      color: '#FFFFFF',
      lineHeight: 1.2,
    },
    caption: {
      fontSize: 12,
      fontWeight: '500',
      color: 'rgba(255, 255, 255, 0.8)',
      lineHeight: 1.3,
    },
    micro: {
      fontSize: 10,
      fontWeight: '600',
      color: 'rgba(255, 255, 255, 0.7)',
      lineHeight: 1.2,
    },

    // Numbers and Stats
    number: {
      fontSize: 20,
      fontWeight: '800',
      color: '#FFFFFF',
      lineHeight: 1,
    },
    stat: {
      fontSize: 18,
      fontWeight: '700',
      color: '#FFFFFF',
      lineHeight: 1.1,
    },
  },

  // Layout & Spacing
  spacing: {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
    xxl: 48,
  },

  // Border Radius
  radius: {
    sm: 8,
    md: 12,
    lg: 16,
    xl: 20,
    round: 999,
  },

  // Shadows
  shadows: {
    soft: {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 2,
    },
    medium: {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.15,
      shadowRadius: 8,
      elevation: 4,
    },
    strong: {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 8 },
      shadowOpacity: 0.2,
      shadowRadius: 16,
      elevation: 8,
    },
    glow: {
      shadowColor: '#6366F1',
      shadowOffset: { width: 0, height: 0 },
      shadowOpacity: 0.3,
      shadowRadius: 12,
      elevation: 6,
    },
  },

  // Component Styles
  components: {
    // Glassmorphism Cards
    card: {
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
      borderRadius: 16,
      padding: 20,
      borderWidth: 1,
      borderColor: 'rgba(255, 255, 255, 0.2)',
      backdropFilter: 'blur(10px)',
    },

    // Premium Feature Cards
    featureCard: {
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
      borderRadius: 16,
      padding: 20,
      borderWidth: 1,
      borderColor: 'rgba(255, 255, 255, 0.2)',
      alignItems: 'center',
    },

    // Icon Containers
    iconContainer: {
      width: 48,
      height: 48,
      borderRadius: 12,
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: 12,
    },

    // Buttons
    primaryButton: {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      borderRadius: 12,
      paddingVertical: 16,
      paddingHorizontal: 24,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 1,
      borderColor: 'rgba(255, 255, 255, 0.3)',
    },

    // Premium Badge
    premiumBadge: {
      backgroundColor: 'rgba(245, 158, 11, 0.2)',
      borderRadius: 20,
      paddingVertical: 6,
      paddingHorizontal: 12,
      borderWidth: 1,
      borderColor: 'rgba(245, 158, 11, 0.3)',
    },
  },

  // Animation Values
  animations: {
    duration: {
      fast: 200,
      normal: 300,
      slow: 500,
    },
    easing: {
      ease: 'ease',
      easeIn: 'ease-in',
      easeOut: 'ease-out',
      easeInOut: 'ease-in-out',
    },
  },
};

/**
 * Platform-specific adaptations
 */
export const getAdaptedDesign = (platform: keyof typeof PlatformProfiles) => {
  const capabilities = PlatformProfiles[platform];
  const baseDesign = KingdomBibleQuizDesign;

  return {
    ...baseDesign,

    // Adapt glassmorphism based on platform support
    components: {
      ...baseDesign.components,

      card: {
        ...baseDesign.components.card,
        backdropFilter: capabilities.supportsBlur ? 'blur(10px)' : undefined,
        backgroundColor: capabilities.supportsTransparency
          ? 'rgba(255, 255, 255, 0.15)'
          : '#FFFFFF',
      },

      featureCard: {
        ...baseDesign.components.featureCard,
        backdropFilter: capabilities.supportsBlur ? 'blur(10px)' : undefined,
        backgroundColor: capabilities.supportsTransparency
          ? 'rgba(255, 255, 255, 0.15)'
          : '#FFFFFF',
      },
    },

    // Adapt shadows based on platform support
    shadows: capabilities.supportsShadows
      ? baseDesign.shadows
      : {
          soft: {},
          medium: {},
          strong: {},
        },

    // Platform-specific overrides
    platform: {
      name: platform,
      capabilities,
      maxImageSize: capabilities.maxImageSize,
      aspectRatio: capabilities.preferredAspectRatio,
    },
  };
};

/**
 * CSS-in-JS generator for web platforms
 */
export const generateCSS = (platform: keyof typeof PlatformProfiles) => {
  const design = getAdaptedDesign(platform);

  return `
    /* Kingdom Bible Quiz - Universal Design System */
    /* Platform: ${platform} */

    :root {
      --kbq-primary: ${design.colors.primary};
      --kbq-secondary: ${design.colors.secondary};
      --kbq-glass-primary: ${design.colors.glass.primary};
      --kbq-glass-border: ${design.colors.glass.border};
      --kbq-text-primary: ${design.colors.text.primary};
      --kbq-radius-lg: ${design.radius.lg}px;
      --kbq-spacing-md: ${design.spacing.md}px;
      --kbq-spacing-lg: ${design.spacing.lg}px;
    }

    .kbq-card {
      background: ${design.components.card.backgroundColor};
      border-radius: ${design.components.card.borderRadius}px;
      padding: ${design.components.card.padding}px;
      border: ${design.components.card.borderWidth}px solid ${design.components.card.borderColor};
      ${design.components.card.backdropFilter ? `backdrop-filter: ${design.components.card.backdropFilter};` : ''}
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    .kbq-feature-card {
      background: ${design.components.featureCard.backgroundColor};
      border-radius: ${design.components.featureCard.borderRadius}px;
      padding: ${design.components.featureCard.padding}px;
      border: ${design.components.featureCard.borderWidth}px solid ${design.components.featureCard.borderColor};
      ${design.components.featureCard.backdropFilter ? `backdrop-filter: ${design.components.featureCard.backdropFilter};` : ''}
      text-align: center;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    .kbq-heading {
      font-size: ${design.typography.heading.fontSize}px;
      font-weight: ${design.typography.heading.fontWeight};
      color: ${design.typography.heading.color};
      line-height: ${design.typography.heading.lineHeight};
      letter-spacing: ${design.typography.heading.letterSpacing}px;
    }

    .kbq-body {
      font-size: ${design.typography.body.fontSize}px;
      font-weight: ${design.typography.body.fontWeight};
      color: ${design.typography.body.color};
      line-height: ${design.typography.body.lineHeight};
    }

    .kbq-gradient-bg {
      background: linear-gradient(135deg, ${design.colors.gradients.main.join(', ')});
    }
  `;
};

/**
 * React Native StyleSheet generator
 */
export const generateReactNativeStyles = (platform: keyof typeof PlatformProfiles) => {
  const design = getAdaptedDesign(platform);

  return {
    // Container styles
    container: {
      backgroundColor: '#FFFFFF',
      ...(platform === 'whatsapp' ? {
        minHeight: '100vh',
        width: '100vw',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: 'auto',
      } : {
        flex: 1,
      }),
    },

    // Glassmorphism card
    premiumCard: design.components.card,

    // Feature cards grid
    premiumFeaturesGrid: {
      width: '100%',
      marginTop: design.spacing.lg,
      marginBottom: design.spacing.lg,
      gap: design.spacing.md,
    },

    premiumFeatureRow: {
      flexDirection: 'row',
      gap: design.spacing.md,
      justifyContent: 'space-between',
    },

    premiumFeatureCard: {
      ...design.components.featureCard,
      flex: 1,
      ...design.shadows.medium,
    },

    // Typography
    heading: design.typography.heading,
    subheading: design.typography.subheading,
    body: design.typography.body,
    caption: design.typography.caption,

    // Icon containers
    featureIcon: {
      ...design.components.iconContainer,
      ...design.shadows.soft,
    },

    // Premium badge
    premiumBadge: {
      ...design.components.premiumBadge,
      flexDirection: 'row',
      alignItems: 'center',
      alignSelf: 'center',
      gap: design.spacing.xs,
    },
  };
};

export default KingdomBibleQuizDesign;
